import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";

import AmplifyError from "components/AmplifyError/AmplifyError";
import ISystemSettings from "interfaces/ISystemSettings";

import "styles/sf-base.scss";
import "styles/sf-calendar.scss";
import "styles/sf-dropdown.scss";
import IUpdateSystemSettingsRequestModel from "interfaces/Request/IUpdateSystemSettingsRequestModel";
import { updateSystemSettings } from "actions/updateSystemSettings";
import IUpdateSystemSettingsResponseModel from "interfaces/Response/IUpdateSystemSettingsResponseModel";
import systemSettingsSchema from "modules/schemas/systemSettingsSchema";
import IGetSystemSettingsResponseModel from "interfaces/Response/IGetSystemSettingsResponseModel";
import useConfig from "hooks/useConfig";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const UpdateSystemSettings = () => {
  const { state } = useStateMachine();

  const { actions } = useStateMachine(
    {
      updateSystemSettings
    }
  );

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setError,
    watch
  } = useForm<IUpdateSystemSettingsRequestModel>({
    resolver: yupResolver(systemSettingsSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const watchFields = watch(["portalName", "logoFile", "siteTimeout", "mobileSuffix"]);

  const {
    isSending: getSystemSettingsIsSending,
    errors: getSystemSettingsErrors,
    data: getSystemSettingsData,
    sendRequest: getSystemSettingsSendRequest
  } = useAmplifyREST();

  const {
    isSending: updateSystemSettingsIsSending,
    errors: updateSystemSettingsErrors,
    data: updateSystemSettingsData,
    statusCode: updateSystemSettingsStatusCode,
    sendRequest: updateSystemSettingsSendRequest
  } = useAmplifyREST();

  const apiErrors = getSystemSettingsErrors || updateSystemSettingsErrors;
  const isSending = getSystemSettingsIsSending || updateSystemSettingsIsSending;

  const navigate = useNavigate();

  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [systemSettings, setSystemSettings] = useState<ISystemSettings|undefined>();
  const [updateSystemSettingsRequest, setUpdateSystemSettingsRequest] = useState<IUpdateSystemSettingsRequestModel>();

  const canFormBeSubmitted = () => {
    const portalName = getValues("portalName");
    const logoFile = getValues("logoFile");
    const siteTimeout = getValues("siteTimeout");
    const mobileSuffix = getValues("mobileSuffix");

    let atLeastOneFieldChangedValue = false;

    const requiredFieldsEntered = portalName.trim().length > 0
      && siteTimeout > 0
      && mobileSuffix.trim().length > 0;

    if (requiredFieldsEntered) {
      atLeastOneFieldChangedValue = portalName !== systemSettings?.portalName
        || logoFile?.length > 0
        || Number(siteTimeout) !== Number(systemSettings?.siteTimeout!)
        || mobileSuffix !== systemSettings?.mobileSuffix;
    }

    setUpdateButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  useEffect(() => {
    if (systemSettings) {
      canFormBeSubmitted();
    }
  }, [watchFields, canFormBeSubmitted, systemSettings]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    getSystemSettingsSendRequest("settings/system", "GET");
  }, [getSystemSettingsSendRequest]);

  useEffect(() => {
    if (getSystemSettingsData) {
      setSystemSettings((getSystemSettingsData as IGetSystemSettingsResponseModel).systemSettings);
    }
  }, [getSystemSettingsData]);

  const handleUpdateSystemSettingsUpdateButtonClick = (
    request: IUpdateSystemSettingsRequestModel
  ) => {
    if (request.logoFile.length > 0) {
      if (request.logoFile[0].size > useConfig().logoMaxFileSizeInBytes!) {
        setError("logoFile", {
          message: `The size of the logo file must not exceed ${useConfig().logoMaxFileSizeInBytes / 1000000}MB.`
        });
        return;
      }
      const fileExtension = request.logoFile[0].name.includes(".") ? request.logoFile[0].name.split(".").pop().toLocaleLowerCase() : "";

      if (!useConfig().logoAllowedExtensions.includes(fileExtension)) {
        setError("logoFile", { message: `Invalid logo file.  File extension must be one of the following ${useConfig().logoAllowedExtensions.join(", ")}.` });
        return;
      }
    }

    setUpdateSystemSettingsRequest(request);
    const formData = new FormData();
    formData.append("logoFile", request.logoFile[0]);
    formData.append("portalName", request.portalName);
    formData.append("mobileSuffix", request.mobileSuffix);
    formData.append("siteTimeout", request.siteTimeout.toString());
    updateSystemSettingsSendRequest(
      "settings/system",
      "PUT",
      formData
    );
  };

  useEffect(() => {
    if (updateSystemSettingsStatusCode === 200 && updateSystemSettingsRequest && updateSystemSettingsData) {
      actions.updateSystemSettings((updateSystemSettingsData as IUpdateSystemSettingsResponseModel).systemSettings);

      navigate(RoutePathsEnum.Settings);
    }
  }, [updateSystemSettingsStatusCode, updateSystemSettingsRequest, updateSystemSettingsData]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
    }
  }, [apiErrors]);

  const renderSiteTimeoutValues = () : any => {
    const options = [
      <option key="" value="">
        Please Select
      </option>
    ];

    const siteTimeouts = useConfig().siteTimeoutValuesInMinutes;
    for (let i = 0; i < siteTimeouts.length; i++) {
      options.push(<option key={`siteTimeout_${siteTimeouts[i]}`} value={siteTimeouts[i]}>{`${siteTimeouts[i]} mins`}</option>);
    }

    return (options);
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-system-settings">
              System Settings
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleUpdateSystemSettingsUpdateButtonClick)}
        >
          {systemSettings && (
            <Card.Body className="no-padding">
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={8} className="d-flex">
                  <Breadcrumb className="left-margin-large no-wrap">
                    <Breadcrumb.Item
                      data-testid="breadcrumb-settings"
                      linkAs={Link}
                      linkProps={{ to: RoutePathsEnum.Settings }}
                      onClick={() => navigate(RoutePathsEnum.Settings)}
                    >
                      Settings
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      active
                      data-testid="breadcrumb-update-system-settings"
                    >
                      Edit System Settings
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
                <Col className="d-lg-flex justify-content-end user-filter">
                  <IconButton
                    iconType={IconButtonType.Save}
                    variant="primary"
                    className="right-margin-small"
                    disabled={
                      updateButtonDisabled || updateSystemSettingsIsSending
                    }
                    aria-disabled={
                      updateButtonDisabled || updateSystemSettingsIsSending
                    }
                    aria-label="Update System Settings"
                    type="submit"
                    spin={isSending}
                    data-testid="update"
                  >
                    Update
                  </IconButton>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                    data-testid="cancel"
                    aria-label="Cancel update and back to Settings"
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Divider />
              <section className="pt-2" data-testid="formElements">
                <Container fluid>
                  <Row className="d-flex mt-1 ms-1">
                    <Col xs={12}>
                      <div className="control-pane">
                        <div className="control-section" />
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="portalName">
                            <Form.Label
                              id="portalNameLabel"
                              className="horiz-form-label"
                            >
                              Portal Name:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={systemSettings?.portalName}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "portalName",
                                  errors,
                                  null
                                )}
                                aria-labelledby="portalNameLabel"
                                maxLength={50}
                                {...register("portalName")}
                                onKeyUp={() => canFormBeSubmitted()}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="portalName"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="logoFile">
                            <Form.Label
                              id="logoFileLabel"
                              className="horiz-form-label"
                            >
                              Logo File:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                {...register("logoFile")}
                                type="file"
                                aria-required="false"
                                aria-invalid={ariaInvalidCheck(
                                  "logoFile",
                                  errors,
                                  null
                                )}
                                aria-labelledby="logoFileLabel"
                                required={false}
                                accept=".gif,.jpg,.jpeg,.png"
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="logoFile"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="siteTimeout">
                            <Form.Label
                              id="siteTimeoutLabel"
                              className="horiz-form-label"
                            >
                              Site Timeout:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("siteTimeout", {
                                  onChange: () => canFormBeSubmitted()
                                })}
                                defaultValue={systemSettings?.siteTimeout}
                                data-testid="ddlSiteTimeout"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="siteTimeoutLabel"
                              >
                                {renderSiteTimeoutValues()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages
                              fieldName="siteTimeout"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="mobileSuffix">
                            <Form.Label
                              id="mobileSuffixLabel"
                              className="horiz-form-label"
                            >
                              Mobile Suffix:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                defaultValue={systemSettings?.mobileSuffix}
                                aria-required="true"
                                aria-invalid={ariaInvalidCheck(
                                  "mobileSuffix",
                                  errors,
                                  null
                                )}
                                aria-labelledby="mobileSuffixLabel"
                                maxLength={30}
                                {...register("mobileSuffix")}
                                onKeyUp={() => canFormBeSubmitted()}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="mobileSuffix"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>

                      </div>
                    </Col>
                  </Row>
                </Container>
              </section>
            </Card.Body>
          ) }
        </Form>
      </Card>
    </Page>
  );
};

export default UpdateSystemSettings;
